import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Bike, 
  Car, 
  Truck, 
  Bus, 
  Caravan, 
  Container,
  ChevronRight,
  Clock,
  CheckCircle2,
  AlertCircle,
  Percent
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  bike: Bike,
  car: Car,
  truck: Truck,
  bus: Bus,
  caravan: Caravan,
  container: Container
};

const colorMap = {
  "B": "from-blue-500 to-blue-700",
  "B Auto": "from-cyan-500 to-blue-600",
  "B Extins": "from-green-500 to-emerald-600",
  "B+E": "from-teal-500 to-cyan-600",
  "C": "from-amber-500 to-orange-600",
  "C+E": "from-slate-600 to-slate-800",
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

export default function ServicesPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (err) {
        setError("Nu am putut încărca categoriile");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="services-page">
      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Serviciile Noastre
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 mt-4 tracking-tight font-['Archivo']">
              Categorii de Permis
            </h1>
            <p className="text-slate-600 mt-6 text-lg">
              Alege categoria potrivită pentru tine și începe pregătirea pentru permisul de conducere.
              Oferim cursuri complete pentru toate categoriile.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-slate-200 rounded-xl mb-6" />
                    <div className="h-8 bg-slate-200 rounded mb-4" />
                    <div className="h-4 bg-slate-200 rounded mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-slate-600">{error}</p>
            </div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon] || Car;
                const gradientColor = colorMap[category.code] || "from-blue-500 to-blue-700";
                
                return (
                  <motion.div key={category.id} variants={itemVariants}>
                    <Card 
                      data-testid={`service-card-${category.code}`}
                      className={`h-full border hover:shadow-xl transition-all duration-300 overflow-hidden group relative ${
                        category.promo_active ? 'border-green-500 ring-2 ring-green-200' : 'border-slate-200 hover:border-blue-500/50'
                      }`}
                    >
                      {/* Promo Badge */}
                      {category.promo_active && (
                        <div className="absolute top-4 right-4 z-10 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                          <Percent className="w-3 h-3" />
                          {category.promo_text || "PROMOȚIE"}
                        </div>
                      )}
                      {/* Gradient Header */}
                      <div className={`bg-gradient-to-r ${gradientColor} p-6 text-white`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <Badge className="bg-white/20 text-white border-0 mb-2">
                              Categoria
                            </Badge>
                            <h2 className="text-3xl font-black font-['Archivo']">
                              {category.code}
                            </h2>
                          </div>
                          <IconComponent className="w-16 h-16 opacity-80 group-hover:scale-110 transition-transform" />
                        </div>
                      </div>
                      
                      <CardContent className="p-6 space-y-6">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900 mb-2 font-['Archivo']">
                            {category.name}
                          </h3>
                          <p className="text-slate-600">
                            {category.description}
                          </p>
                        </div>
                        
                        {/* Duration & Price */}
                        <div className={`grid gap-4 ${category.price_min > 0 || category.price_max > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                          <div className="bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                              <Clock className="w-4 h-4" />
                              Durată
                            </div>
                            <p className="font-semibold text-slate-900">{category.duration || "2-3 luni"}</p>
                          </div>
                          {(category.price_min > 0 || category.price_max > 0) && (
                            <div className="bg-blue-50 rounded-lg p-3">
                              <div className="text-blue-600 text-sm mb-1">Preț estimat</div>
                              {category.promo_active && category.original_price_range ? (
                                <div>
                                  <p className="text-slate-400 line-through text-sm">{category.original_price_range}</p>
                                  <p className="font-bold text-green-600">{category.price_range}</p>
                                </div>
                              ) : (
                                <p className="font-semibold text-slate-900">{category.price_range}</p>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Requirements */}
                        {category.requirements && category.requirements.length > 0 && (
                          <div>
                            <p className="text-sm font-semibold text-slate-700 mb-3">Cerințe:</p>
                            <ul className="space-y-2">
                              {category.requirements.map((req, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  {req}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        <Link to="/programari" className="block">
                          <Button 
                            data-testid={`book-category-${category.code}`}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                          >
                            Programează-te
                            <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-16 md:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight font-['Archivo']">
              Servicii Incluse
            </h2>
            <p className="text-slate-600 mt-4">
              Toate cursurile noastre includ aceste beneficii
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Pregătire Teoretică", desc: "Cursuri complete pentru examenul teoretic" },
              { title: "Fișă Medicală", desc: "Te ajutăm să obții fișa medicală" },
              { title: "Aviz Psihologic", desc: "Suport pentru avizul psihologic" },
              { title: "Program Flexibil", desc: "Ore adaptate la programul tău" },
            ].map((service, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full border border-slate-200 hover:border-blue-500/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 text-sm">{service.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-['Archivo']">
            Ai întrebări despre categorii?
          </h2>
          <p className="text-blue-100 mt-4">
            Contactează-ne și te ajutăm să alegi categoria potrivită pentru tine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/contact">
              <Button 
                data-testid="services-contact-btn"
                size="lg" 
                className="bg-white text-blue-600 hover:bg-blue-50 font-semibold"
              >
                Contactează-ne
              </Button>
            </Link>
            <Link to="/programari">
              <Button 
                data-testid="services-booking-btn"
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 font-semibold bg-transparent"
              >
                Programează o Consultație
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
